import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase-config';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { languageManifest as baseLanguageManifest } from '../locales';

const PORTAL_LANG_DOC = doc(db, 'settings', 'portalLanguages');

export const AI_LANGUAGE_CATALOG = [
  { code: 'af', label: 'Afrikaans', nativeName: 'Afrikaans', dir: 'ltr' },
  { code: 'am', label: 'Amharic', nativeName: 'አማርኛ', dir: 'ltr' },
  { code: 'ar', label: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'az', label: 'Azerbaijani', nativeName: 'Azərbaycan dili', dir: 'ltr' },
  { code: 'bg', label: 'Bulgarian', nativeName: 'Български', dir: 'ltr' },
  { code: 'bn', label: 'Bengali', nativeName: 'বাংলা', dir: 'ltr' },
  { code: 'ca', label: 'Catalan', nativeName: 'Català', dir: 'ltr' },
  { code: 'cs', label: 'Czech', nativeName: 'Čeština', dir: 'ltr' },
  { code: 'da', label: 'Danish', nativeName: 'Dansk', dir: 'ltr' },
  { code: 'de', label: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'el', label: 'Greek', nativeName: 'Ελληνικά', dir: 'ltr' },
  { code: 'en', label: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'es', label: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'et', label: 'Estonian', nativeName: 'Eesti', dir: 'ltr' },
  { code: 'fa', label: 'Persian', nativeName: 'فارسی', dir: 'rtl' },
  { code: 'fi', label: 'Finnish', nativeName: 'Suomi', dir: 'ltr' },
  { code: 'fil', label: 'Filipino', nativeName: 'Filipino', dir: 'ltr' },
  { code: 'fr', label: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'gu', label: 'Gujarati', nativeName: 'ગુજરાતી', dir: 'ltr' },
  { code: 'he', label: 'Hebrew', nativeName: 'עברית', dir: 'rtl' },
  { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr' },
  { code: 'hr', label: 'Croatian', nativeName: 'Hrvatski', dir: 'ltr' },
  { code: 'hu', label: 'Hungarian', nativeName: 'Magyar', dir: 'ltr' },
  { code: 'id', label: 'Indonesian', nativeName: 'Bahasa Indonesia', dir: 'ltr' },
  { code: 'it', label: 'Italian', nativeName: 'Italiano', dir: 'ltr' },
  { code: 'ja', label: 'Japanese', nativeName: '日本語', dir: 'ltr' },
  { code: 'kn', label: 'Kannada', nativeName: 'ಕನ್ನಡ', dir: 'ltr' },
  { code: 'ko', label: 'Korean', nativeName: '한국어', dir: 'ltr' },
  { code: 'lt', label: 'Lithuanian', nativeName: 'Lietuvių', dir: 'ltr' },
  { code: 'lv', label: 'Latvian', nativeName: 'Latviešu', dir: 'ltr' },
  { code: 'ml', label: 'Malayalam', nativeName: 'മലയാളം', dir: 'ltr' },
  { code: 'mr', label: 'Marathi', nativeName: 'मराठी', dir: 'ltr' },
  { code: 'ms', label: 'Malay', nativeName: 'Bahasa Melayu', dir: 'ltr' },
  { code: 'nl', label: 'Dutch', nativeName: 'Nederlands', dir: 'ltr' },
  { code: 'no', label: 'Norwegian', nativeName: 'Norsk', dir: 'ltr' },
  { code: 'pl', label: 'Polish', nativeName: 'Polski', dir: 'ltr' },
  { code: 'pt', label: 'Portuguese', nativeName: 'Português', dir: 'ltr' },
  { code: 'ro', label: 'Romanian', nativeName: 'Română', dir: 'ltr' },
  { code: 'ru', label: 'Russian', nativeName: 'Русский', dir: 'ltr' },
  { code: 'sk', label: 'Slovak', nativeName: 'Slovenčina', dir: 'ltr' },
  { code: 'sl', label: 'Slovenian', nativeName: 'Slovenščina', dir: 'ltr' },
  { code: 'sr', label: 'Serbian', nativeName: 'Српски', dir: 'ltr' },
  { code: 'sv', label: 'Swedish', nativeName: 'Svenska', dir: 'ltr' },
  { code: 'sw', label: 'Swahili', nativeName: 'Kiswahili', dir: 'ltr' },
  { code: 'ta', label: 'Tamil', nativeName: 'தமிழ்', dir: 'ltr' },
  { code: 'te', label: 'Telugu', nativeName: 'తెలుగు', dir: 'ltr' },
  { code: 'th', label: 'Thai', nativeName: 'ไทย', dir: 'ltr' },
  { code: 'tl', label: 'Filipino', nativeName: 'Filipino', dir: 'ltr' },
  { code: 'tr', label: 'Turkish', nativeName: 'Türkçe', dir: 'ltr' },
  { code: 'uk', label: 'Ukrainian', nativeName: 'Українська', dir: 'ltr' },
  { code: 'ur', label: 'Urdu', nativeName: 'اردو', dir: 'rtl' },
  { code: 'vi', label: 'Vietnamese', nativeName: 'Tiếng Việt', dir: 'ltr' },
  { code: 'zh', label: 'Chinese (Simplified)', nativeName: '简体中文', dir: 'ltr' },
];

const normalizeCode = (value) => String(value || '').trim().toLowerCase();

const mergedManifest = (customLanguages = {}) => {
  const normalizedCustom = Object.entries(customLanguages).reduce((acc, [k, v]) => {
    const code = normalizeCode(k);
    if (!code || !v || typeof v !== 'object') return acc;
    acc[code] = {
      label: v.label || code.toUpperCase(),
      nativeName: v.nativeName || v.label || code.toUpperCase(),
      dir: v.dir === 'rtl' ? 'rtl' : 'ltr'
    };
    return acc;
  }, {});
  return { ...baseLanguageManifest, ...normalizedCustom };
};

export const useManagedLanguages = () => {
  const [customLanguages, setCustomLanguages] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(PORTAL_LANG_DOC, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setCustomLanguages(data.languages || {});
    });
    return () => unsub();
  }, []);

  const languageManifest = useMemo(() => mergedManifest(customLanguages), [customLanguages]);

  const languageEntries = useMemo(
    () =>
      Object.entries(languageManifest).sort((a, b) =>
        (a[1].label || a[0]).localeCompare(b[1].label || b[0])
      ),
    [languageManifest]
  );

  const availableToAdd = useMemo(() => {
    const activeCodes = new Set(Object.keys(languageManifest).map(normalizeCode));
    return AI_LANGUAGE_CATALOG
      .filter((item) => !activeCodes.has(normalizeCode(item.code)))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [languageManifest]);

  const addLanguage = async (code) => {
    const normalized = normalizeCode(code);
    if (!normalized) return;
    const selected = AI_LANGUAGE_CATALOG.find((x) => normalizeCode(x.code) === normalized);
    if (!selected) return;
    await setDoc(
      PORTAL_LANG_DOC,
      {
        languages: {
          [normalized]: {
            label: selected.label,
            nativeName: selected.nativeName,
            dir: selected.dir === 'rtl' ? 'rtl' : 'ltr',
            addedAt: serverTimestamp()
          }
        },
        lastUpdatedAt: serverTimestamp()
      },
      { merge: true }
    );
  };

  return { languageManifest, languageEntries, availableToAdd, addLanguage };
};
